import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  CheckCircle, 
  FileText, 
  User, 
  Calendar, 
  Star,
  AlertCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PCWSignPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [employeeComments, setEmployeeComments] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    fetchEvaluation();
  }, [token]);

  const fetchEvaluation = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pcw-sign/${token}`);
      setData(response.data);
      if (response.data.already_acknowledged) {
        setAcknowledged(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Evaluation not found or link expired");
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/pcw-sign/${token}/acknowledge`, {
        employee_comments: employeeComments
      });
      toast.success("Evaluation acknowledged successfully!");
      setAcknowledged(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to acknowledge evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      1: "text-emerald-600 bg-emerald-50",
      2: "text-green-600 bg-green-50",
      3: "text-yellow-600 bg-yellow-50",
      4: "text-orange-600 bg-orange-50",
      5: "text-red-600 bg-red-50",
    };
    return colors[grade] || "text-slate-600 bg-slate-50";
  };

  const getGradeLabel = (grade) => {
    const labels = { 1: "Excellent", 2: "Very Good", 3: "Average", 4: "Below Average", 5: "Poor" };
    return labels[grade] || "N/A";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Invalid or Expired</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            If you believe this is an error, please contact your supervisor.
          </p>
        </div>
      </div>
    );
  }

  const { evaluation, agency_name, duties, grading_scale } = data;

  if (acknowledged) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Evaluation Acknowledged</h1>
          <p className="text-slate-600 mb-6">
            Thank you for reviewing and acknowledging your performance evaluation.
          </p>
          <p className="text-sm text-slate-500">
            You may close this page. A copy has been saved to your employee record.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Performance Evaluation</h1>
              <p className="text-slate-500">{agency_name}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Employee</p>
              <p className="font-medium text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                {evaluation.employee_name}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Evaluation Date</p>
              <p className="font-medium text-slate-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                {new Date(evaluation.evaluation_date).toLocaleDateString()}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Evaluator</p>
              <p className="font-medium text-slate-900">{evaluation.evaluator_name}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Average Rating</p>
              <p className={`font-bold text-lg ${getGradeColor(Math.round(evaluation.average_rating)).split(' ')[0]}`}>
                {evaluation.average_rating?.toFixed(1)} ({getGradeLabel(Math.round(evaluation.average_rating))})
              </p>
            </div>
          </div>

          {evaluation.period_start && evaluation.period_end && (
            <p className="text-sm text-slate-500 mt-4">
              Evaluation Period: {evaluation.period_start} to {evaluation.period_end}
            </p>
          )}
        </div>

        {/* Grading Scale */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <h3 className="font-medium text-slate-700 mb-3">Grading Scale</h3>
          <div className="flex flex-wrap gap-2">
            {grading_scale.map((grade) => (
              <span key={grade.value} className={`px-3 py-1 rounded-full text-sm ${getGradeColor(grade.value)}`}>
                {grade.value} = {grade.label}
              </span>
            ))}
          </div>
        </div>

        {/* Duties Evaluation */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            PCW Duties Evaluation
          </h2>
          
          <div className="space-y-3">
            {duties.map((duty) => {
              const rating = evaluation[duty.id];
              return (
                <div key={duty.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-500 mr-2">#{duty.number}</span>
                    <span className="text-slate-700">{duty.description}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(rating)}`}>
                    {rating} - {getGradeLabel(rating)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Goals */}
        {(evaluation.goal_1 || evaluation.goal_2 || evaluation.goal_3) && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Goals Set by RN/Supervisor</h2>
            <ul className="space-y-2">
              {evaluation.goal_1 && (
                <li className="flex items-start gap-2">
                  <span className="text-teal-600 font-medium">1.</span>
                  <span className="text-slate-700">{evaluation.goal_1}</span>
                </li>
              )}
              {evaluation.goal_2 && (
                <li className="flex items-start gap-2">
                  <span className="text-teal-600 font-medium">2.</span>
                  <span className="text-slate-700">{evaluation.goal_2}</span>
                </li>
              )}
              {evaluation.goal_3 && (
                <li className="flex items-start gap-2">
                  <span className="text-teal-600 font-medium">3.</span>
                  <span className="text-slate-700">{evaluation.goal_3}</span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Evaluator Comments */}
        {evaluation.additional_comments && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Evaluator Comments</h2>
            <p className="text-slate-700 whitespace-pre-wrap">{evaluation.additional_comments}</p>
          </div>
        )}

        {/* Employee Comments & Acknowledgment */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Response</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Comments (Optional)
              </label>
              <Textarea
                value={employeeComments}
                onChange={(e) => setEmployeeComments(e.target.value)}
                placeholder="Add any comments or feedback about your evaluation..."
                rows={4}
                className="w-full"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> By clicking "Acknowledge Evaluation" below, you confirm that you have 
                reviewed your performance evaluation. This does not necessarily mean you agree with all ratings, 
                but that you have seen and discussed them with your supervisor.
              </p>
            </div>

            <Button 
              onClick={handleAcknowledge}
              disabled={submitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg"
              data-testid="acknowledge-evaluation-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Acknowledging...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Acknowledge Evaluation
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500">
          If you have questions about this evaluation, please contact your supervisor at {agency_name}.
        </p>
      </div>
    </div>
  );
}
